import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';

interface WeeklySummary {
  success: boolean;
  weekStart: string;
  weekEnd: string;
  daysWithData: number;
  averages: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fats_g: number;
    fiber_g: number;
    sodium_mg: number;
    potassium_mg: number;
  };
  macroPercentages: {
    protein_g: number;
    carbs_g: number;
    fats_g: number;
  };
}

interface Goal {
  goal_id: string;
  goal_name: string;
  goal_value: number;
  goal_type: 'over' | 'under' | 'target';
  variance_percent?: number;
  unit: string;
  health_data_field: string;
  category: string;
  status: string;
  visibility: string;
  highlight_for_client?: boolean;
}

interface GoalDisplay {
  name: string;
  average: number;
  target: number;
  unit: string;
  goalType: 'over' | 'under' | 'target';
  variance?: number;
  isPracticeMode: boolean; // T2 = true (disabled), T3 = false (enabled)
}

/**
 * This Week Tile - Shows weekly nutrition summary for clients
 *
 * Displays:
 * - Average daily calories
 * - Macro breakdown donut (P/C/F)
 * - Carb goal progress (if goal exists)
 * - Protein goal progress (if goal exists)
 * - One additional highlighted nutrition goal
 */
export const ThisWeekTile: React.FC = () => {
  const [summary, setSummary] = useState<WeeklySummary | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch weekly summary and goals in parallel
      const [summaryRes, goalsRes] = await Promise.all([
        fetch('/api/nutrition/weekly-summary', { headers: getAuthHeaders() }),
        fetch('/api/goals', { headers: getAuthHeaders() })
      ]);

      // Handle weekly summary
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        if (summaryData.success) {
          setSummary(summaryData);
        }
      }

      // Handle goals
      if (goalsRes.ok) {
        const goalsData = await goalsRes.json();
        if (goalsData.success) {
          // Filter to nutrition goals that are visible (T2+)
          const nutritionGoals = Object.values(goalsData.goals || {})
            .filter((g: any) =>
              g.category === 'nutrition' &&
              g.visibility === 'visible'
            ) as Goal[];
          setGoals(nutritionGoals);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error loading This Week data:', err);
      setError('Unable to load weekly data');
    } finally {
      setLoading(false);
    }
  };

  // Map health_data_field to weekly summary averages keys (canonical names)
  const fieldToAverageKey: Record<string, keyof WeeklySummary['averages']> = {
    'calories': 'calories',
    'protein_g': 'protein_g',
    'carbs_g': 'carbs_g',
    'fats_g': 'fats_g',
    'fiber_g': 'fiber_g',
    'sodium_mg': 'sodium_mg',
    'potassium_mg': 'potassium_mg'
  };

  // Build display goals: Only show goals with highlight_for_client === true
  const getDisplayGoals = (): GoalDisplay[] => {
    if (!summary) return [];

    const displayGoals: GoalDisplay[] = [];

    // Only show goals that have highlight_for_client enabled
    const highlightedGoals = goals.filter(g => g.highlight_for_client === true);

    for (const goal of highlightedGoals) {
      const avgKey = fieldToAverageKey[goal.health_data_field];
      if (avgKey && summary.averages[avgKey] !== undefined) {
        displayGoals.push({
          name: goal.goal_name.replace('Daily ', ''),
          average: summary.averages[avgKey],
          target: goal.goal_value,
          unit: goal.unit,
          goalType: goal.goal_type,
          variance: goal.variance_percent,
          isPracticeMode: goal.status === 'disabled'
        });
      }
    }

    return displayGoals;
  };

  const getGoalLabel = (goal: GoalDisplay): string => {
    const op = goal.goalType === 'under' ? '<' : goal.goalType === 'over' ? '≥' : '~';
    return `${op} ${goal.target}${goal.unit}`;
  };

  // Calculate bar percentage and color for goal progress
  const getGoalBarStatus = (goal: GoalDisplay): { percent: number; color: string } => {
    const { average, target, goalType } = goal;
    const ratio = average / target;
    const percent = Math.min(ratio * 100, 120); // Cap at 120% for display

    if (goalType === 'under') {
      // Price is Right model: under-eating is also a problem
      if (ratio <= 0.50) return { percent, color: 'purple-dark' };   // Severely under
      if (ratio <= 0.70) return { percent, color: 'purple-light' };  // Under-eating
      if (ratio <= 1.00) return { percent, color: 'green' };         // Sweet spot
      if (ratio <= 1.10) return { percent, color: 'yellow' };        // Slightly over
      return { percent: 120, color: 'red' };                         // Over budget
    } else if (goalType === 'over') {
      // Over goals: red when way under, orange approaching, green when met
      if (ratio >= 1.00) return { percent: Math.min(percent, 100), color: 'green' };
      if (ratio >= 0.90) return { percent, color: 'orange' };
      return { percent, color: 'red' };
    } else {
      // Target goals: use variance
      const variancePct = goal.variance || 10;
      const min = 1 - variancePct / 100;
      const max = 1 + variancePct / 100;
      if (ratio >= min && ratio <= max) return { percent: Math.min(percent, 100), color: 'green' };
      if (ratio >= min * 0.9 && ratio <= max * 1.1) return { percent, color: 'yellow' };
      return { percent, color: 'red' };
    }
  };

  const displayGoals = getDisplayGoals();

  if (loading) {
    return (
      <div className="progress-card this-week-tile loading">
        <div className="card-header">
          <Activity size={18} className="card-icon" />
          <h3>This Week</h3>
        </div>
        <div className="card-content">
          <div className="loading-skeleton">Loading...</div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="progress-card this-week-tile empty">
        <div className="card-header">
          <Activity size={18} className="card-icon" />
          <h3>This Week</h3>
        </div>
        <div className="card-content empty-content">
          <p className="empty-text">No nutrition data this week</p>
          <p className="empty-hint">Log meals in FatSecret to see your weekly summary</p>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-card this-week-tile">
      <div className="card-header">
        <Activity size={18} className="card-icon" />
        <h3>This Week</h3>
      </div>

      <div className="card-content">
        {/* Upper section: Calories + Macro Bar */}
        <div className="upper-section">
          <div className="calories-display">
            <span className="calories-value">{summary.averages.calories.toLocaleString()}</span>
            <span className="calories-label">avg cal/day</span>
          </div>
          <div className="macro-stacked-bar">
            <div
              className="macro-segment protein"
              style={{ width: `${summary.macroPercentages.protein_g}%` }}
              title={`Protein: ${summary.averages.protein_g}g (${summary.macroPercentages.protein_g}%)`}
            />
            <div
              className="macro-segment carbs"
              style={{ width: `${summary.macroPercentages.carbs_g}%` }}
              title={`Carbs: ${summary.averages.carbs_g}g (${summary.macroPercentages.carbs_g}%)`}
            />
            <div
              className="macro-segment fat"
              style={{ width: `${summary.macroPercentages.fats_g}%` }}
              title={`Fat: ${summary.averages.fats_g}g (${summary.macroPercentages.fats_g}%)`}
            />
          </div>
          <div className="macro-legend">
            <span className="legend-item">
              <span className="legend-dot protein" />
              P: {summary.averages.protein_g}g ({summary.macroPercentages.protein_g}%)
            </span>
            <span className="legend-item">
              <span className="legend-dot carbs" />
              C: {summary.averages.carbs_g}g ({summary.macroPercentages.carbs_g}%)
            </span>
            <span className="legend-item">
              <span className="legend-dot fat" />
              F: {summary.averages.fats_g}g ({summary.macroPercentages.fats_g}%)
            </span>
          </div>
        </div>

        {/* Goal progress section */}
        {displayGoals.length > 0 && (
          <div className="week-goals-section">
            {displayGoals.map((goal, idx) => {
              const barStatus = getGoalBarStatus(goal);
              return (
                <div key={idx} className="goal-row">
                  <div className="goal-header">
                    <span className={`goal-name ${goal.isPracticeMode ? 'practice-mode' : ''}`}>
                      {goal.name}: {Math.round(goal.average)}{goal.unit}
                    </span>
                    <span className="goal-target">{getGoalLabel(goal)}</span>
                  </div>
                  <div className="goal-bar-container">
                    <div
                      className={`goal-bar-fill ${barStatus.color}`}
                      style={{ width: `${Math.min(barStatus.percent, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Days indicator */}
        <div className="week-days-indicator">
          {summary.daysWithData} of 7 days logged
        </div>
      </div>

      <style>{`
        .this-week-tile .card-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .this-week-tile.loading .card-content,
        .this-week-tile.empty .card-content {
          align-items: center;
          justify-content: center;
          min-height: 120px;
        }

        .empty-content {
          text-align: center;
        }

        .empty-text {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 4px 0;
        }

        .empty-hint {
          font-size: 11px;
          color: var(--text-tertiary);
          margin: 0;
        }

        .loading-skeleton {
          font-size: 13px;
          color: var(--text-tertiary);
        }

        /* Upper section - calories + macro bar grouped tightly */
        .upper-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        /* Calories display */
        .calories-display {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .calories-value {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.1;
        }

        .calories-label {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
        }

        .macro-stacked-bar {
          width: 100%;
          display: flex;
          height: 12px;
          margin-top: 4px;
          border-radius: 6px;
          overflow: hidden;
          background: var(--bg-tertiary);
        }

        .macro-segment {
          height: 100%;
          transition: width 0.3s ease;
        }

        .macro-segment.protein { background: #ef4444; }
        .macro-segment.carbs { background: #3b82f6; }
        .macro-segment.fat { background: #f59e0b; }

        .macro-legend {
          display: flex;
          justify-content: center;
          gap: 12px;
          font-size: 11px;
          color: var(--text-secondary);
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .legend-dot.protein { background: #ef4444; }
        .legend-dot.carbs { background: #3b82f6; }
        .legend-dot.fat { background: #f59e0b; }

        /* Goals section */
        .week-goals-section {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }

        .goal-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .goal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }

        .goal-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .goal-name.practice-mode {
          color: var(--accent-blue, #3b82f6);
        }

        .goal-target {
          font-size: 11px;
          color: var(--text-secondary);
        }

        .goal-bar-container {
          height: 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
          overflow: hidden;
        }

        .goal-bar-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .goal-bar-fill.green { background: #10b981; }
        .goal-bar-fill.yellow { background: #f59e0b; }
        .goal-bar-fill.orange { background: #f97316; }
        .goal-bar-fill.red { background: #ef4444; }
        .goal-bar-fill.purple-dark { background: #7c3aed; }
        .goal-bar-fill.purple-light { background: #a78bfa; }

        /* Days indicator */
        .week-days-indicator {
          font-size: 10px;
          color: var(--text-tertiary);
          text-align: center;
          padding-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default ThisWeekTile;
